// Real-Time SSE Resilience
// ------------------------
// Recovers live updates after the SSE connection drops or is suspended — most
// commonly when a mobile browser freezes a backgrounded/locked tab and tears
// down the EventSource. The server only pushes *future* events (current state
// is rendered server-side on a full page load), so a reconnected stream never
// re-sends "the current question". This module closes that gap WITHOUT interval
// polling: it watches connection health and re-syncs ONLY on an actual loss or
// on resume.
//
// Triggers (all event-driven — zero network traffic when healthy or hidden):
//   - the tab becomes visible again        (visibilitychange)
//   - the network comes back               (online)
//   - htmx reports a stream error          (htmx:sseError)
//   - heartbeat watchdog: the server sends a `ping` every ~15s; if none has
//     arrived for STALE_MS while the tab is VISIBLE, the connection is presumed
//     dead -> resync. The watchdog only reads a local timestamp; it issues NO
//     request unless it finds a gap, and is stopped entirely while hidden.
//
// On any trigger it dispatches a single (debounced) `realtime-resync` event on
// <body>. Consumers opt in declaratively, so this file stays decoupled:
//   - play-page fragments: hx-trigger="..., realtime-resync from:body"
//   - notification bell:   notifications-realtime.js re-fetches on the event
//
// Re-establishing the live channel is left to native EventSource auto-reconnect
// (which fires only when the connection is actually lost); this module only
// restores the STATE that was missed during the gap.

(function () {
    'use strict';

    var STALE_MS = 35000;            // ~2 missed 15s pings => presumed dead
    var WATCHDOG_MS = 10000;         // local liveness check cadence (no network)
    var RESYNC_DEBOUNCE_MS = 5000;   // collapse bursts of triggers

    var lastBeat = 0;                // timestamp of the last SSE signal
    var everOpened = false;          // an EventSource has opened at least once
    var lastResync = 0;
    var watchdogTimer = null;

    function now() { return Date.now(); }
    function beat() { lastBeat = now(); }

    function resync(reason) {
        var t = now();
        if (t - lastResync < RESYNC_DEBOUNCE_MS) return;
        lastResync = t;
        try {
            var target = document.body || document.documentElement;
            target.dispatchEvent(new CustomEvent('realtime-resync', {
                bubbles: true,
                detail: { reason: reason }
            }));
        } catch (e) { /* no-op */ }
    }

    // Heartbeat: piggyback on htmx's EventSource(s). htmx fires htmx:sseOpen
    // with event.detail.source on every (re)connect; we bump the heartbeat on
    // each `ping` from any stream. Stable fn ref keeps addEventListener
    // idempotent when sseOpen re-fires for the same source on reconnect.
    function onBeat() { beat(); }

    document.addEventListener('htmx:sseOpen', function (event) {
        everOpened = true;
        beat();
        var source = event.detail && event.detail.source;
        if (source && typeof source.addEventListener === 'function') {
            source.addEventListener('ping', onBeat);
            source.addEventListener('message', onBeat);
        }
    });

    // htmx surfaces a dropped/erroring stream here.
    document.addEventListener('htmx:sseError', function () {
        resync('sse-error');
    });

    // Watchdog: runs ONLY while visible; pure local check, no network unless a
    // gap is found. Fully stopped when hidden so a backgrounded/forgotten tab
    // does nothing.
    function checkHeartbeat() {
        if (document.hidden || !everOpened) return;
        if (now() - lastBeat > STALE_MS) {
            resync('heartbeat-stale');
            // Treat as a fresh beat so a persistently dead connection can't spin
            // into a tight resync loop — wait another full interval first.
            beat();
        }
    }

    function startWatchdog() {
        if (watchdogTimer) return;
        watchdogTimer = setInterval(checkHeartbeat, WATCHDOG_MS);
    }

    function stopWatchdog() {
        if (watchdogTimer) { clearInterval(watchdogTimer); watchdogTimer = null; }
    }

    // Resume triggers.
    document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
            stopWatchdog();
        } else {
            beat();             // freshly visible; let native reconnect run
            resync('visible');  // catch up on anything missed while hidden
            startWatchdog();
        }
    });

    window.addEventListener('online', function () { resync('online'); });
    window.addEventListener('beforeunload', stopWatchdog);

    function init() {
        if (!document.hidden) startWatchdog();
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
